/**
 * Host adapter: junkdrawer email APIs → theme ``EmailArchiveData`` (TR-3993).
 *
 * Junkdrawer has no folder model, so the tree is a single synthetic mailbox
 * node. ``bodyHtml`` is sanitized here before it reaches the theme viewer
 * (which renders it in a sandboxed iframe).
 */

import { parseFragment, serialize } from 'parse5';
import type {
  EmailAddress,
  EmailArchiveAttachment,
  EmailArchiveContact,
  EmailArchiveData,
  EmailArchiveFormat,
  EmailArchiveMessage,
  EmailArchiveTimelineEvent,
  EmailArchiveTreeNode,
} from '@tranquility-ai/theme-components/email-archive-viewer';
import type {
  JunkdrawerEmailAttachment,
  JunkdrawerEmailDetail,
  JunkdrawerEmailListResponse,
  JunkdrawerEmailSearchResult,
  JunkdrawerEmailSummary,
} from '@/shared/cors-junkdrawer-api';
import { parseDisplayName, parseEmailAddress, s3KeyBasename } from './utils';

/** Stable id for the single synthetic mailbox tree node. */
export const MAILBOX_NODE_ID = 'mailbox';

/** Default page size for ``GET /v1/emails`` when driving ``onLoadMore``. */
export const EMAIL_ARCHIVE_PAGE_SIZE = 50;

const ALLOWED_LINK_SCHEMES = /^(?:https?:|mailto:|cid:|tel:|#)/i;
/** Remote loads, including protocol-relative ``//host/...`` (TR-3993). */
const REMOTE_URL = /^\s*(?:https?:|\/\/)/i;
const DROP_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'meta',
  'base',
  'form',
]);

type HtmlAttr = { name: string; value: string };
type HtmlNode = {
  nodeName: string;
  tagName?: string;
  attrs?: HtmlAttr[];
  childNodes?: HtmlNode[];
};

function keepAttribute(tag: string, name: string, value: string): boolean {
  const attr = name.toLowerCase();
  if (attr.startsWith('on') || attr === 'srcset' || attr.startsWith('data-')) return false;
  const urlAttr = attr === 'href' || attr === 'src' || attr === 'action' || attr === 'formaction' || attr === 'xlink:href';
  if (urlAttr && /^\s*javascript:/i.test(value)) return false;
  if ((attr === 'src' || attr === 'xlink:href') && REMOTE_URL.test(value)) return false;
  if (attr === 'href' && tag !== 'a' && REMOTE_URL.test(value)) return false;
  if (attr === 'href' && tag === 'a' && value.trim() !== '' && !ALLOWED_LINK_SCHEMES.test(value.trim())) {
    return false;
  }
  return true;
}

function sanitizeNode(node: HtmlNode): HtmlNode | null {
  if (node.nodeName === '#comment') return null;
  if (node.nodeName === '#text') return node;
  const tag = (node.tagName ?? node.nodeName).toLowerCase();
  if (DROP_TAGS.has(tag)) return null;
  if (node.attrs) {
    node.attrs = node.attrs.filter((attr) => keepAttribute(tag, attr.name, attr.value));
    for (const attr of node.attrs) {
      if (attr.name.toLowerCase() === 'style') {
        attr.value = attr.value.replace(/url\(\s*['"]?\s*(?:https?:|\/\/)[^)]*\)/gi, 'none');
      }
    }
  }
  if (node.childNodes) {
    node.childNodes = node.childNodes
      .map((child) => sanitizeNode(child))
      .filter((child): child is HtmlNode => child != null);
  }
  return node;
}

/**
 * Scrub an email body before the theme viewer puts it in a sandboxed iframe.
 * Drops the same tags and remote-image URLs as the Outlook viewer's DOMPurify
 * pass, including handlers the old regex missed (``<svg/onload>``). Remote
 * images stay blocked — there is no "show images" opt-in on this path.
 */
export function sanitizeEmailBodyHtml(html: string): string {
  if (!html) return '';
  const fragment = parseFragment(html) as HtmlNode;
  sanitizeNode(fragment);
  return serialize(fragment as never);
}

function plainSnippet(body: string): string | undefined {
  const text = body
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text ? text.slice(0, 160) : undefined;
}

function looksLikeHtml(body: string): boolean {
  return /<\/?(?:html|body|div|p|br|table|td|tr|span|a|img|h[1-6]|ul|ol|li|strong|em|b|i)[\s/>]/i.test(
    body,
  );
}

function isJsonBlob(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (trimmed.startsWith('{')) return true;
  if (!trimmed.startsWith('[')) return false;
  // List previews such as ``[EXTERNAL]`` or ``[Image]`` are not JSON arrays.
  if (/^\[[^[\]{}",:\d][^[\]{}",:]{0,79}\](?:\s|$)/.test(trimmed)) return false;
  return true;
}

type EmbeddedEmailFields = {
  subject?: string;
  fromAddress?: string;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  date?: string;
  dateRaw?: string;
  body?: string;
};

function stringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
  return out.length ? out : undefined;
}

/** An object counts as an email only when it has a sender or a subject. */
function asEmbeddedEmail(value: unknown): EmbeddedEmailFields | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const obj = value as Record<string, unknown>;
  const subject = typeof obj.subject === 'string' ? obj.subject.trim() : '';
  const fromAddress = typeof obj.fromAddress === 'string' ? obj.fromAddress.trim() : '';
  if (!subject && !fromAddress) return null;
  return {
    subject: subject || undefined,
    fromAddress: fromAddress || undefined,
    toAddresses: stringList(obj.toAddresses),
    ccAddresses: stringList(obj.ccAddresses),
    bccAddresses: stringList(obj.bccAddresses),
    date: typeof obj.date === 'string' ? obj.date : undefined,
    dateRaw: typeof obj.dateRaw === 'string' ? obj.dateRaw : undefined,
    body: typeof obj.body === 'string' ? obj.body : undefined,
  };
}

function collectEmbeddedEmails(value: unknown): EmbeddedEmailFields[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectEmbeddedEmails(item));
  }
  if (!value || typeof value !== 'object') return [];
  const obj = value as Record<string, unknown>;
  const nested = ['emails', 'messages', 'email', 'message'].flatMap((key) =>
    obj[key] != null ? collectEmbeddedEmails(obj[key]) : [],
  );
  if (nested.length) return nested;
  const direct = asEmbeddedEmail(value);
  return direct ? [direct] : [];
}

/**
 * Some ingested mailbox rows store the file metadata JSON (or a list of
 * emails) in ``body`` / ``bodySnippet`` instead of the message text. Pull the
 * email fields back out so the viewer can list and open them.
 */
export function embeddedEmailsFromBody(body: string): EmbeddedEmailFields[] {
  const trimmed = body.trim();
  if (!isJsonBlob(trimmed)) return [];
  try {
    return collectEmbeddedEmails(JSON.parse(trimmed));
  } catch {
    return [];
  }
}

function applyEmbeddedEmail(
  message: EmailArchiveMessage,
  email: EmbeddedEmailFields,
): EmailArchiveMessage {
  const next: EmailArchiveMessage = { ...message };
  if (!next.subject && email.subject) next.subject = email.subject;
  if (!next.from && email.fromAddress) next.from = toEmailAddress(email.fromAddress);
  if (!next.to?.length && email.toAddresses) next.to = toEmailAddressList(email.toAddresses);
  if (!next.cc?.length && email.ccAddresses) next.cc = toEmailAddressList(email.ccAddresses);
  if (!next.timestamp && (email.date || email.dateRaw)) {
    next.timestamp = email.date ?? email.dateRaw;
  }
  const inner = email.body ?? '';
  if (!inner) return next;
  if (looksLikeHtml(inner)) {
    next.bodyHtml = sanitizeEmailBodyHtml(inner);
    next.bodyText = undefined;
  } else {
    next.bodyText = inner;
    next.bodyHtml = undefined;
  }
  next.snippet = plainSnippet(inner);
  return next;
}

/** Map an RFC 5322-ish address string into the theme ``EmailAddress`` shape. */
export function toEmailAddress(raw: string | null | undefined): EmailAddress | undefined {
  if (!raw || !raw.trim()) return undefined;
  const address = parseEmailAddress(raw);
  if (!address) return undefined;
  const name = parseDisplayName(raw);
  if (!name || name === '(no sender)' || name === address) {
    return { address };
  }
  return { name, address };
}

export function toEmailAddressList(addresses: string[] | null | undefined): EmailAddress[] {
  if (!addresses?.length) return [];
  const out: EmailAddress[] = [];
  for (const raw of addresses) {
    const parsed = toEmailAddress(raw);
    if (parsed) out.push(parsed);
  }
  return out;
}

export function messageIdForIndex(emailIndex: number): string {
  return `msg-${emailIndex}`;
}

export function attachmentIdFor(
  emailIndex: number,
  attachmentIndex: number,
): string {
  return `att-${emailIndex}-${attachmentIndex}`;
}

/** Infer archive format from the mailbox path/filename. */
export function detectArchiveFormat(s3Key: string): EmailArchiveFormat | undefined {
  const base = s3KeyBasename(s3Key).toLowerCase();
  const match = base.match(/\.(mbox|mbx|pst|ost|eml|emlx|msg)$/);
  return (match?.[1] as EmailArchiveFormat | undefined) ?? undefined;
}

export function mapJunkdrawerAttachment(
  attachment: JunkdrawerEmailAttachment,
  emailIndex: number,
): EmailArchiveAttachment {
  return {
    id: attachmentIdFor(emailIndex, attachment.attachmentIndex),
    filename: attachment.fileName,
    mimeType: attachment.contentType ?? undefined,
    sizeBytes: attachment.contentLength,
    src: attachment.attachmentUrl,
  };
}

/**
 * Reconstruct a junkdrawer attachment row from a theme attachment (or tree
 * leaf) so the in-viewer pane can open it via MediaDisplay.
 */
export function junkdrawerAttachmentFromThemeItem(
  item: EmailArchiveAttachment | EmailArchiveTreeNode,
): JunkdrawerEmailAttachment | null {
  if ('filename' in item) {
    const src = item.src?.trim();
    if (!src) return null;
    const idMatch = item.id.match(/^att-(\d+)-(\d+)$/);
    return {
      attachmentIndex: idMatch ? Number(idMatch[2]) : 0,
      attachmentUrl: src,
      fileName: item.filename,
      contentType: item.mimeType ?? null,
      contentLength: item.sizeBytes ?? 0,
    };
  }
  if (item.kind === 'attachment' && item.mediaSrc) {
    const idMatch = item.id.match(/^att-(\d+)-(\d+)$/);
    return {
      attachmentIndex: idMatch ? Number(idMatch[2]) : 0,
      attachmentUrl: item.mediaSrc,
      fileName: item.label,
      contentType: item.mediaType ?? null,
      contentLength: 0,
    };
  }
  return null;
}

export function mapJunkdrawerSummaryToMessage(
  summary: JunkdrawerEmailSummary,
): EmailArchiveMessage {
  const snippet =
    summary.bodySnippet && !isJsonBlob(summary.bodySnippet) ? summary.bodySnippet : undefined;
  const message: EmailArchiveMessage = {
    id: messageIdForIndex(summary.emailIndex),
    subject: summary.subject ?? undefined,
    from: toEmailAddress(summary.fromAddress),
    to: toEmailAddressList(summary.toAddresses),
    timestamp: summary.date ?? summary.dateRaw ?? undefined,
    snippet,
    bodyText: snippet,
    folderId: MAILBOX_NODE_ID,
  };
  if (summary.bodySnippet && isJsonBlob(summary.bodySnippet)) {
    const [embedded] = embeddedEmailsFromBody(summary.bodySnippet);
    if (embedded) return applyEmbeddedEmail(message, embedded);
  }
  return message;
}

export function mapJunkdrawerDetailToMessage(
  detail: JunkdrawerEmailDetail,
): EmailArchiveMessage {
  const body = detail.body ?? '';
  const attachments = detail.attachments.map((a) =>
    mapJunkdrawerAttachment(a, detail.emailIndex),
  );
  const message: EmailArchiveMessage = {
    id: messageIdForIndex(detail.emailIndex),
    subject: detail.subject ?? undefined,
    from: toEmailAddress(detail.fromAddress),
    to: toEmailAddressList(detail.toAddresses),
    cc: toEmailAddressList(detail.ccAddresses),
    timestamp: detail.date ?? detail.dateRaw ?? undefined,
    snippet: body ? plainSnippet(body) : undefined,
    attachments,
    folderId: MAILBOX_NODE_ID,
  };

  const embedded = body ? embeddedEmailsFromBody(body) : [];
  if (embedded.length === 1) {
    return applyEmbeddedEmail(message, embedded[0]);
  }

  if (body) {
    if (looksLikeHtml(body)) {
      message.bodyHtml = sanitizeEmailBodyHtml(body);
    } else {
      message.bodyText = body;
    }
  }

  return message;
}

export function mapJunkdrawerSearchHitToMessage(
  hit: JunkdrawerEmailSearchResult,
): EmailArchiveMessage {
  const mapped = mapJunkdrawerDetailToMessage(hit);
  if (hit.matchedText) {
    mapped.snippet = hit.matchedText;
  }
  return mapped;
}

/**
 * Junkdrawer has no folders — synthesize one mailbox node so the theme tree
 * rail has something to render.
 */
export function synthesizeMailboxTree(args: {
  s3Key: string;
  messageCount?: number;
  label?: string;
  messages?: EmailArchiveMessage[];
}): EmailArchiveTreeNode[] {
  const label = args.label?.trim() || s3KeyBasename(args.s3Key) || 'Mailbox';
  const node: EmailArchiveTreeNode = {
    id: MAILBOX_NODE_ID,
    label,
    path: args.s3Key,
    kind: 'mailbox',
  };
  if (args.messageCount != null) {
    node.badge = args.messageCount;
  }
  if (args.messages?.length) {
    node.children = args.messages.map((message) => ({
      id: message.id,
      label:
        message.subject?.trim() ||
        message.from?.name ||
        message.from?.address ||
        message.id,
      kind: 'message' as const,
    }));
  }
  return [node];
}

export function listPageHasMore(list: JunkdrawerEmailListResponse): boolean {
  return list.offset + list.emails.length < list.total;
}

function deriveContacts(messages: EmailArchiveMessage[]): EmailArchiveContact[] {
  const byAddress = new Map<string, EmailArchiveContact>();
  for (const message of messages) {
    const candidates = [
      message.from,
      ...(message.to ?? []),
      ...(message.cc ?? []),
    ].filter(Boolean) as EmailAddress[];
    for (const addr of candidates) {
      const key = addr.address.toLowerCase();
      const existing = byAddress.get(key);
      if (existing) {
        existing.messageCount = (existing.messageCount ?? 0) + 1;
        if (!existing.name && addr.name) existing.name = addr.name;
      } else {
        byAddress.set(key, {
          id: `contact-${key}`,
          name: addr.name,
          addresses: [addr.address],
          messageCount: 1,
        });
      }
    }
  }
  return Array.from(byAddress.values());
}

function deriveTimeline(messages: EmailArchiveMessage[]): EmailArchiveTimelineEvent[] {
  return messages
    .filter((m) => m.timestamp)
    .map((m) => ({
      id: `evt-${m.id}`,
      timestamp: m.timestamp as string,
      description: m.subject
        ? `Message: ${m.subject}`
        : `Message from ${m.from?.address ?? 'unknown'}`,
      category: 'received',
      messageId: m.id,
    }));
}

function collectAttachments(messages: EmailArchiveMessage[]): EmailArchiveAttachment[] {
  const out: EmailArchiveAttachment[] = [];
  for (const message of messages) {
    if (message.attachments?.length) out.push(...message.attachments);
  }
  return out;
}

/**
 * Assemble a full ``EmailArchiveData`` payload from a list page and/or already
 * mapped messages (including search hits and detail-enriched rows).
 */
export function buildEmailArchiveData(args: {
  s3Key: string;
  /** Browse-mode list metadata (total, date range). */
  list?: JunkdrawerEmailListResponse | null;
  /** Messages already mapped (summaries, details, and/or search hits). */
  messages: EmailArchiveMessage[];
  title?: string;
}): EmailArchiveData {
  const title =
    args.title?.trim() ||
    s3KeyBasename(args.s3Key) ||
    'Email archive';
  const messageCount = args.list?.total ?? args.messages.length;
  const dateFrom = args.list?.dateFrom ?? undefined;
  const dateTo = args.list?.dateTo ?? undefined;

  return {
    title,
    archive: {
      format: detectArchiveFormat(args.s3Key),
      messageCount,
      ...(dateFrom || dateTo
        ? { dateRange: { from: dateFrom ?? undefined, to: dateTo ?? undefined } }
        : {}),
    },
    tree: synthesizeMailboxTree({
      s3Key: args.s3Key,
      messageCount,
      label: title,
      messages: args.messages,
    }),
    messages: args.messages,
    attachments: collectAttachments(args.messages),
    contacts: deriveContacts(args.messages),
    timeline: deriveTimeline(args.messages),
  };
}

/**
 * Replace (or append) a detail-mapped message in a list of summaries by id.
 * A body that embeds several emails expands into one row per email.
 */
export function mergeMessageDetail(
  messages: EmailArchiveMessage[],
  detail: JunkdrawerEmailDetail,
): EmailArchiveMessage[] {
  const embedded = embeddedEmailsFromBody(detail.body ?? '');
  if (embedded.length > 1) {
    const parentId = messageIdForIndex(detail.emailIndex);
    const belongsToParent = (id: string) => id === parentId || id.startsWith(`${parentId}-`);
    const expanded = embedded.map((email, index) =>
      applyEmbeddedEmail(
        {
          id: index === 0 ? parentId : `${parentId}-${index}`,
          folderId: MAILBOX_NODE_ID,
        },
        email,
      ),
    );
    const idx = messages.findIndex((m) => belongsToParent(m.id));
    const rest = messages.filter((m) => !belongsToParent(m.id));
    if (idx < 0) return [...rest, ...expanded];
    const insertAt = messages.slice(0, idx).filter((m) => !belongsToParent(m.id)).length;
    return [...rest.slice(0, insertAt), ...expanded, ...rest.slice(insertAt)];
  }

  const mapped = mapJunkdrawerDetailToMessage(detail);
  const idx = messages.findIndex((m) => m.id === mapped.id);
  if (idx < 0) return [...messages, mapped];
  const previous = messages[idx];
  const next = messages.slice();
  next[idx] = {
    ...previous,
    ...mapped,
    snippet: isJsonBlob(previous.snippet) ? mapped.snippet : previous.snippet || mapped.snippet,
  };
  return next;
}
